// Bonus Types form rules (Payroll Settings > Bonus), from Cauver Bonus spec
// v1.6 B3 / B12 and the Bonus API doc. Rows are the API's type objects
// (see pages/PayRoll/Shared/bonusApi.js).

export const METHOD_OPTIONS = [
  { value: "percent_of_wages", label: "% of wages", helper: "A percentage of wages over a period" },
  { value: "fixed_or_days", label: "Fixed amount / days' wages", helper: "Same amount, or X days of wages" },
  { value: "per_day", label: "Fixed amount per day", helper: "₹ × days present or paid days" },
  { value: "enter_amount", label: "Enter amount", helper: "Typed in or imported for each person" },
  { value: "formula", label: "Custom formula", helper: "Build your own rule" },
];

export const DAYS_BASIS_OPTIONS = [
  { value: "days_present", label: "Days Present" },
  { value: "paid_days", label: "Paid Days" },
];

export const PERIOD_OPTIONS = [
  { value: "fy", label: "Financial year (Apr–Mar)" },
  { value: "cy", label: "Calendar year (Jan–Dec)" },
  { value: "month", label: "Month" },
  { value: "custom", label: "Custom" },
];

export const DIVISOR_OPTIONS = [
  { value: "26", label: "26" },
  { value: "30", label: "30" },
  { value: "actual", label: "Actual days in month" },
];

export const CALCULATION_BASIS_OPTIONS = [
  { value: "fixed_amount", label: "Fixed amount", helper: "The same amount, once per employee per run" },
  { value: "days_wages", label: "Days' wages", helper: "X days of the To month's wages" },
];

export const STATUTORY_NOTICE =
  "Cauver works this bonus out from the rules you enter. Check they match the law that applies to you; Cauver does not certify compliance.";

export const MAX_AMOUNT = 1000000;
export const MAX_FORMULA_LENGTH = 500;

// Which fields each method uses (spec B3). Fields that don't apply are not
// shown and not sent (the server clears them).
const PERIOD_METHODS = ["percent_of_wages", "per_day", "formula"];
export const usesPeriod = (method) => PERIOD_METHODS.includes(method);
export const usesPartPayments = (method) => PERIOD_METHODS.includes(method);
export const usesMinDays = (method) => PERIOD_METHODS.includes(method);
export const usesWageComponents = (form) =>
  form.method === "percent_of_wages" || (form.method === "fixed_or_days" && form.calculationBasis === "days_wages");

export const EMPTY_BONUS_TYPE = {
  name: "",
  isStatutory: false,
  method: "",
  rate: "",
  wageComponents: [],
  ceiling: "",
  wageLimit: "",
  minDays: "",
  daysBasis: "",
  perDayAmount: "",
  calculationBasis: "",
  fixedAmount: "",
  days: "",
  dayDivisor: "",
  formula: "",
  defaultPeriod: "fy",
  allowPart: false,
  payComponentId: "",
  active: true,
};

const str = (value) => (value === null || value === undefined ? "" : String(value));
// "7000.00" -> "7000", "8.33" -> "8.33" so the form shows what was typed.
const numStr = (value) => (value === null || value === undefined || value === "" ? "" : String(Number(value)));

export const toForm = (row) => ({
  name: row.name || "",
  isStatutory: Boolean(row.is_statutory),
  method: row.method || "",
  rate: numStr(row.rate),
  wageComponents: Array.isArray(row.wage_components) ? row.wage_components : [],
  ceiling: numStr(row.ceiling),
  wageLimit: numStr(row.wage_limit),
  minDays: numStr(row.min_days),
  daysBasis: str(row.days_basis),
  perDayAmount: numStr(row.per_day_amount),
  calculationBasis: str(row.calculation_basis),
  fixedAmount: numStr(row.fixed_amount),
  days: numStr(row.days),
  dayDivisor: row.day_divisor == null ? "" : (row.day_divisor === "actual" ? "actual" : numStr(row.day_divisor)),
  formula: row.formula || "",
  defaultPeriod: row.default_period || "fy",
  allowPart: Boolean(row.allow_part),
  payComponentId: row.pay_component_id ?? "",
  active: row.status !== "Inactive",
});

const blank = (value) => String(value ?? "").trim() === "";
const money = (value) => (blank(value) ? null : String(Number(value)));
const count = (value) => (blank(value) ? null : Number(value));

// Full payload for the form's method, in the API's names.
export const toPayload = (form) => {
  const payload = {
    name: form.name.trim(),
    is_statutory: Boolean(form.isStatutory),
    method: form.method,
    pay_component_id: form.payComponentId === "" ? null : Number(form.payComponentId),
    status: form.active ? "Active" : "Inactive",
  };
  const m = form.method;
  if (m === "percent_of_wages") {
    payload.rate = money(form.rate);
    payload.ceiling = money(form.ceiling);
    payload.wage_limit = money(form.wageLimit);
  }
  if (usesWageComponents(form)) payload.wage_components = form.wageComponents;
  if (m === "per_day") payload.per_day_amount = money(form.perDayAmount);
  if (usesMinDays(m)) payload.min_days = count(form.minDays);
  if (m === "per_day" || (usesMinDays(m) && !blank(form.minDays))) payload.days_basis = form.daysBasis || null;
  if (m === "fixed_or_days") {
    payload.calculation_basis = form.calculationBasis || null;
    if (form.calculationBasis === "fixed_amount") payload.fixed_amount = money(form.fixedAmount);
    if (form.calculationBasis === "days_wages") {
      payload.days = money(form.days);
      payload.day_divisor = form.dayDivisor || null;
    }
  }
  if (m === "formula") payload.formula = form.formula.trim();
  if (usesPeriod(m)) payload.default_period = form.defaultPeriod;
  if (usesPartPayments(m)) payload.allow_part = Boolean(form.allowPart);
  return payload;
};

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// PATCH body: only what changed against the saved type (spec: "send only
// what changed"). Changing method sends the whole new method's fields.
export const changedPayload = (form, row) => {
  const next = toPayload(form);
  if (next.method !== row.method) return next;
  const before = toPayload(toForm(row));
  return Object.fromEntries(Object.entries(next).filter(([key, value]) => !same(value, before[key])));
};

const decimals = (value) => (String(value).split(".")[1] || "").length;
const num = (value) => Number(value);
const isNum = (value) => !blank(value) && Number.isFinite(Number(value));

// Client checks with the server's ranges (spec B3 numeric ranges). The
// server checks again; its field errors are shown too.
export const validateBonusType = (form, rows, editingId) => {
  const errors = {};
  const name = form.name.trim();
  if (!name) errors.name = "Name is required.";
  else if (name.length > 100) errors.name = "Name can have at most 100 characters.";
  else if (rows.some((row) => row.id !== editingId && String(row.name).trim().toLowerCase() === name.toLowerCase())) {
    errors.name = "A bonus type with this name already exists.";
  }
  const m = form.method;
  if (!m) errors.method = "Select how the bonus is worked out.";

  const amount = (key, label, { required, max = MAX_AMOUNT } = {}) => {
    const value = form[key];
    if (blank(value)) {
      if (required) errors[key] = `${label} is required.`;
      return;
    }
    if (!isNum(value) || num(value) <= 0) errors[key] = `${label} must be more than 0.`;
    else if (num(value) > max) errors[key] = `${label} can be at most ₹${max.toLocaleString("en-IN")}.`;
    else if (decimals(value) > 2) errors[key] = `${label} can have at most 2 decimals.`;
  };

  if (m === "percent_of_wages") {
    if (blank(form.rate)) errors.rate = "Bonus rate % is required.";
    else if (!isNum(form.rate) || num(form.rate) < 0.01 || num(form.rate) > 100) errors.rate = "Bonus rate % must be between 0.01 and 100.";
    else if (decimals(form.rate) > 2) errors.rate = "Bonus rate % can have at most 2 decimals.";
    amount("ceiling", "Wage ceiling", { max: Infinity });
    amount("wageLimit", "Wage limit", { max: Infinity });
  }
  if (usesWageComponents(form) && !form.wageComponents.length) errors.wageComponents = "Select wages used.";
  if (m === "per_day") amount("perDayAmount", "Amount per day", { required: true });
  if (usesMinDays(m) && !blank(form.minDays)) {
    if (!isNum(form.minDays) || num(form.minDays) < 0 || num(form.minDays) > 366) errors.minDays = "Minimum days must be between 0 and 366.";
  }
  if ((m === "per_day" || (usesMinDays(m) && !blank(form.minDays))) && !form.daysBasis) {
    errors.daysBasis = "Select days counted (Days Present or Paid Days).";
  }
  if (m === "fixed_or_days") {
    if (!form.calculationBasis) errors.calculationBasis = "Select the calculation basis.";
    if (form.calculationBasis === "fixed_amount") amount("fixedAmount", "Fixed amount", { required: true });
    if (form.calculationBasis === "days_wages") {
      if (blank(form.days)) errors.days = "Days is required.";
      else if (!isNum(form.days) || num(form.days) < 0.5 || num(form.days) > 365) errors.days = "Days must be between 0.5 and 365.";
      if (!form.dayDivisor) errors.dayDivisor = "Select the divisor.";
    }
  }
  if (m === "formula") {
    if (!form.formula.trim()) errors.formula = "Formula is required.";
    else if (form.formula.length > MAX_FORMULA_LENGTH) errors.formula = "Formula is too long (max 500 characters).";
  }
  if (usesPeriod(m) && !form.defaultPeriod) errors.defaultPeriod = "Select the default period.";
  if (form.payComponentId === "" || form.payComponentId == null) errors.payComponentId = "Select the payslip line.";
  return errors;
};

// Server field names -> form field names.
export const API_FIELD_TO_FORM = {
  name: "name",
  is_statutory: "isStatutory",
  method: "method",
  rate: "rate",
  wage_components: "wageComponents",
  ceiling: "ceiling",
  wage_limit: "wageLimit",
  min_days: "minDays",
  days_basis: "daysBasis",
  per_day_amount: "perDayAmount",
  calculation_basis: "calculationBasis",
  fixed_amount: "fixedAmount",
  days: "days",
  day_divisor: "dayDivisor",
  formula: "formula",
  default_period: "defaultPeriod",
  allow_part: "allowPart",
  pay_component_id: "payComponentId",
  status: "active",
};

// ---------- Table text ----------

export const formatRupees = (value) =>
  value === null || value === undefined || value === "" ? "—" : `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const numberText = (value) => String(Number(value));
const labelOf = (options, value) => options.find((option) => option.value === value)?.label || "";

export const methodLabel = (row) => {
  if (row.method === "fixed_or_days") return row.calculation_basis === "days_wages" ? "Days' wages" : "Fixed amount";
  if (row.method === "per_day") return "Per day";
  return labelOf(METHOD_OPTIONS, row.method) || row.method_label || "—";
};

const wagesText = (row) => {
  const names = row.wage_component_names || [];
  if (!names.length) return "wages";
  return names.length === 1 ? names[0] : `(${names.join(" + ")})`;
};

const daysBasisText = (value) => labelOf(DAYS_BASIS_OPTIONS, value) || "days";

// "Rule" column, e.g. "8.33% of (Basic + DA), wage capped". The API sends no
// summary text, so it is built from the rule fields.
export const ruleSummary = (row) => {
  const minDays = row.min_days != null && row.min_days !== "" ? ` (min ${numberText(row.min_days)} days)` : "";
  switch (row.method) {
    case "percent_of_wages":
      return `${numberText(row.rate)}% of ${wagesText(row)}${row.ceiling ? ", wage capped" : ""}${minDays}`;
    case "fixed_or_days":
      return row.calculation_basis === "days_wages"
        ? `${numberText(row.days)} days of ${wagesText(row)} of the To month`
        : `${formatRupees(row.fixed_amount)} per employee`;
    case "per_day":
      return `${formatRupees(row.per_day_amount)} × ${daysBasisText(row.days_basis)}${minDays}`;
    case "enter_amount":
      return "Typed in or imported";
    case "formula":
      return `${row.formula || "—"}${minDays}`;
    default:
      return "—";
  }
};

export const periodLabel = (row) =>
  usesPeriod(row.method) ? labelOf(PERIOD_OPTIONS, row.default_period) || row.default_period_label || "—" : "—";

export const partPaymentsLabel = (row) => (usesPartPayments(row.method) && row.allow_part ? "Yes" : "No");
